<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';

const { setAttr } = useVisualEditing();
const props = defineProps<{
  data: {
    id: number;
    image?: string | { id: string } | null;
    name?: string | null;
    parallax_enabled?: boolean | null;
    parallax_strength?: number | null;
  };
}>();

// normalize image id
const imageId = computed(() => {
  const img: any = props.data.image;
  return typeof img === 'string' ? img : (img?.id ?? null);
});

const containerRef = ref<HTMLElement | null>(null);

// ⭐️ critical: only allow parallax after mount
const canParallax = ref(false);
onMounted(() => {
  canParallax.value = !!props.data.parallax_enabled;
});

// parallax state (same as before, trimmed)
const targetY = ref(0);
const currentY = ref(0);
const strength = computed(() => props.data.parallax_strength ?? 0.25);
let raf = 0;

const updateTarget = () => {
  const el = containerRef.value;
  if (!el) return;
  const r = el.getBoundingClientRect();
  targetY.value = r.top + r.height * 0.5 - window.innerHeight * 0.5;
};
const tick = () => {
  currentY.value += (targetY.value - currentY.value) * 0.15;
  raf = requestAnimationFrame(tick);
};

onMounted(() => {
  if (!canParallax.value) return;
  updateTarget();
  raf = requestAnimationFrame(tick);
  window.addEventListener('scroll', updateTarget, { passive: true });
  window.addEventListener('resize', updateTarget, { passive: true });
});
onBeforeUnmount(() => {
  cancelAnimationFrame(raf);
  window.removeEventListener('scroll', updateTarget);
  window.removeEventListener('resize', updateTarget);
});

const imgStyle = computed(() => {
  // SSR and first client paint both return **undefined** → no style mismatch
  if (!canParallax.value) return undefined;
  return { transform: `translate3d(0, ${-currentY.value * strength.value}px, 0)` };
});

// robust height (avoid Tailwind purge/HMR issues)
const containerHeight = canParallax.value
  ? '70vh' // 2rem for padding
  : '100vh';
</script>

<template>
  <!-- class="relative w-full max-w-none -mb-16"> -->
  <!-- section.svg_inline ? (isLeft ? 'pl-10px sm:pl-[20px] md:pl-[30px]' : 'pr-10px sm:pr-[20px] md:pr-[30px]') : '' -->
  <section
    ref="containerRef"
    class="relative left-[calc(50%-50vw+10px)] w-[calc(100vw-2*var(--tapis))] sm:left-[calc(50%-50vw+20px)] md:left-[calc(50%-50vw+30px)]"
  >
    <div class="relative w-full overflow-hidden" :style="{ height: containerHeight }">
      <div class="absolute inset-0 will-change-transform" :style="imgStyle">
        <!-- sizes="md:100vw 100vw" -->
        <DirectusImage
          v-if="imageId"
          :uuid="imageId"
          :alt="data.name || 'Image'"
          sizes="xs:100vw sm:100vw md:100vw lg:100vw xl:100vw"
          :preload="{ fetchPriority: 'high' }"
          :placeholder="false"
          class="h-full w-full object-cover"
          :style="{ objectPosition: '20% 30%' }"
          :key="imageId"
          :data-directus="
            setAttr({
              collection: 'block_full_image',
              item: data.id,
              fields: ['image', 'parallax_enabled', 'parallax_strength'],
              mode: 'modal',
            })
          "
        />
        <div v-else class="flex h-full w-full items-center justify-center bg-neutral-100 text-neutral-500">
          Add an image…
        </div>
      </div>
    </div>
  </section>
</template>
